import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_COLORS = {
  new: "bg-brand-red text-white",
  qualified: "bg-brand-pink text-brand-ink",
  shipped: "bg-brand-pinkLight text-brand-ink border border-brand-line",
  closed: "bg-brand-ink text-white",
};

export default function AdminSampleBoxes() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/sample-boxes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/sample-boxes/${id}`, { status });
    toast.success("Status updated");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sample box request?")) return;
    await api.delete(`/admin/sample-boxes/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-sample-boxes-page">
      <div className="eyebrow">Inbox</div>
      <h1 className="font-display text-4xl text-brand-ink mt-2">Sample Box requests</h1>

      <div className="mt-8 space-y-3">
        {items.length === 0 && <div className="text-brand-stone text-sm font-ui">No sample box requests yet.</div>}
        {items.map((s) => (
          <div key={s.id} data-testid={`sb-row-${s.id}`} className="bg-white border border-brand-line rounded-2xl">
            <div className="p-5 flex items-center gap-4 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ${STATUS_COLORS[s.status] || "bg-brand-line"}`}>{s.status}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-ink font-ui">{s.business_name}</div>
                <div className="text-xs text-brand-stone font-ui">{s.contact_name} · {s.business_type} · {new Date(s.created_at).toLocaleDateString()}</div>
              </div>
              <div className="text-xs text-brand-stone hidden lg:flex items-center gap-4 font-ui">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</span>
                {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.city}, {s.state}</span>
              </div>
              <select
                value={s.status}
                onChange={(e) => setStatus(s.id, e.target.value)}
                data-testid={`sb-status-${s.id}`}
                className="h-9 rounded-full border border-brand-line px-3 text-xs bg-white font-ui"
              >
                <option value="new">New</option>
                <option value="qualified">Qualified</option>
                <option value="shipped">Shipped</option>
                <option value="closed">Closed</option>
              </select>
              <button
                onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                data-testid={`sb-toggle-${s.id}`}
                className="w-9 h-9 grid place-items-center rounded-full border border-brand-line"
              >
                {expanded === s.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <button
                onClick={() => remove(s.id)}
                data-testid={`sb-delete-${s.id}`}
                className="w-9 h-9 grid place-items-center rounded-full border border-brand-line hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {expanded === s.id && (
              <div className="border-t border-brand-line p-5 grid md:grid-cols-2 gap-6 text-sm font-ui" data-testid={`sb-details-${s.id}`}>
                <div className="space-y-2">
                  <div><span className="text-brand-stone">Business type:</span> {s.business_type}</div>
                  <div><span className="text-brand-stone">Location:</span> {s.city}, {s.state}{s.zip_code ? ` — ${s.zip_code}` : ""}</div>
                  <div><span className="text-brand-stone">Email:</span> {s.email}</div>
                  <div><span className="text-brand-stone">Phone:</span> {s.phone || "—"}</div>
                </div>
                <div className="space-y-2">
                  <div><span className="text-brand-stone">Currently offers desserts:</span> {s.offers_desserts || "—"}</div>
                  <div><span className="text-brand-stone">Monthly dessert sales:</span> {s.monthly_sales || "—"}</div>
                  <div><span className="text-brand-stone">Locations:</span> {s.locations || "—"}</div>
                </div>
                {s.message && (
                  <div className="md:col-span-2">
                    <div className="text-brand-stone mb-1">Message:</div>
                    <div className="bg-brand-pinkSoft border border-brand-line rounded-xl p-3 whitespace-pre-line">{s.message}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
